import { Component, computed, EventEmitter, inject, Output, signal } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { APP_NAME } from '@core/constants/constant.data'
import { CalculationModuleSignature } from '@core/models/calculation-module.model'
import { CalculationModuleService } from '@core/services/calculation-module.service'
import { CMSessionService } from '@core/services/cm-session.service'
import { SelectionToolService } from '@core/services/selection-tool.service'
import { filterCMOnCategoryPipe } from '@shared/pipes/filter-cm-on-category.pipe'
import Keycloak from 'keycloak-js'
import { CmSessionComponent } from './cm-session/cm-session.component'
import { StandAloneCMsComponent } from './stand-alone-cms/stand-alone-cms.component'
import { STANDALONE_CMS } from './stand-alone-cms/stand-alone-cms.data'
import { ToolchainScenariosComponent } from './toolchain-scenarios/toolchain-scenarios.component'
import { TOOLCHAIN_SCENARIOS } from './toolchain-scenarios/toolchain-scenarios.data'

@Component({
  selector: 'app-cm-list',
  templateUrl: './cm-list.component.html',
  styleUrl: './cm-list.component.scss',
  imports: [
    // Components
    StandAloneCMsComponent,
    CmSessionComponent,
    ToolchainScenariosComponent,

    // Pipes
    filterCMOnCategoryPipe,

    // Forms
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,

    // Material
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
  ],
})
export class CmListComponent {
  @Output() cmSelectedEvent = new EventEmitter<CalculationModuleSignature>()
  readonly keycloak = inject(Keycloak)

  calculationModuleService = inject(CalculationModuleService)
  selectionToolService = inject(SelectionToolService)
  readonly _cmSessionService = inject(CMSessionService)

  calculationModulesState$ = this.calculationModuleService.calculationModulesState$
  calculationModules$ = this.calculationModuleService.calculationModules$

  categories$ = computed(() => {
    return [...new Set(this.calculationModules$().map((cm) => cm.category))] as string[]
  })

  search$ = signal<string>('')
  filteredCalculationModules$ = computed(() => {
    return this.calculationModules$().filter((cm) => {
      if (cm.cm_name?.toLowerCase().includes(this.search$().toLowerCase())) {
        return cm
      }
    })
  })

  toolchainScenarios = TOOLCHAIN_SCENARIOS.filter((cms) => cms.project.includes(APP_NAME))
  standaloneCMs = STANDALONE_CMS.filter((cms) => cms.project.includes(APP_NAME))

  cmSessions$ = this._cmSessionService.cmSessions$

  async ngOnInit() {
    this.calculationModuleService.getCalculationModuleList()
  }

  clearSearch() {
    this.search$.set('')
  }

  selectCM(cm: CalculationModuleSignature) {
    this.cmSelectedEvent.emit(cm)
  }

  formatLevelAvailabilityToolTip(cm: CalculationModuleSignature) {
    if (!cm || !Array.isArray(cm.authorized_scale) || cm.authorized_scale.length === 0) {
      return 'CM available at all levels'
    }

    const formattedLevels =
      cm.authorized_scale.length > 1
        ? cm.authorized_scale.slice(0, -1).join(', ') +
          ' and ' +
          cm.authorized_scale[cm.authorized_scale.length - 1]
        : cm.authorized_scale[0]

    return `CM available at ${formattedLevels} levels`
  }

  unselectAllCMs() {
    this.cmSessions$().forEach((cm) => {
      if (cm.selected == 'true' || cm.selected == 'partially') {
        this._cmSessionService.disableCMSession(cm)
      }
    })
  }
}
